import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import {
    Box,
    Chip,
    Button,
    Tooltip,
    Typography,
    IconButton
} from "@mui/material/";
import { useDispatch, useSelector } from "react-redux";
import { groupBy } from "lodash";
import { getAuditFilterChips } from "../auditFilterUtils";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import { strings } from "../../common/i18n";
import { format, helpers, tableState } from "../../common/lib";
import actions from "../../Store/actions";
import auditStatusDetail from "./auditStatusDetail";
import { useLocation, useNavigate } from "react-router-dom";
import { logToSentry } from "../../config/Sentry";
import ShowMore from "../../common/components/ShowMore";
import AssetProgresBar from "../../common/components/AssetProgresBar";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import styles from "./styles";
import { dateRangeFilterFn } from "../../common/components/MaterialReactTable/filterFns";

const auditStatus = {
    scheduled: "scheduled",
    closed: "closed",
    inprogress: "active",
    archived: "archived",
    draft: "planned"
};

export default () => {
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    let navigate = useNavigate();
    const currentTab = pathname.split("/").pop();

    const cvTablePageSize = localStorage.getItem("cvTablePageSize");
    const defaultRowsPerPage = cvTablePageSize ? parseInt(cvTablePageSize) : 25;

    const { typesList, auditsList, branches, accounts, userOrganisations } =
        useSelector((state) => state);

    const currentStatus = auditStatus[currentTab] ?? auditStatus.inprogress;
    const persistKey = "auditList";

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(defaultRowsPerPage);
    const [rowCount, setRowCount] = useState(0);
    // Namespace on the organisation *id*, matching use-table-state.
    const orgId = userOrganisations?.item?.id;
    const [searchQuery, setSearchQuery] = useState(
        () => tableState.readTableState(persistKey, orgId)?.globalFilter ?? ""
    );
    // The initializer above runs once. On the first sign-in of a session the
    // organisation is still being fetched at that point, so the search box
    // (seeded by use-table-state, which re-reads when the org arrives) would
    // show a restored term while this server-side query stayed empty. Seed it
    // once the org lands — guarded so it can never overwrite user input.
    const searchSeededRef = useRef(Boolean(orgId));
    useEffect(() => {
        if (searchSeededRef.current || !orgId) return;
        searchSeededRef.current = true;
        const stored = tableState.readTableState(persistKey, orgId)
            ?.globalFilter;
        if (stored) setSearchQuery(stored);
    }, [orgId, persistKey]);
    const [loading, setLoading] = useState(false);
    const [currentRow, setCurrentRow] = useState(null);
    const [archiveDialog, setArchiveDialog] = useState(false);
    const [abandonDialog, setAbandonDialog] = useState(false);
    const [abandonRow, setAbandonRow] = useState(null);
    const [selectionResetToken, setSelectionResetToken] = useState(0);
    const [manageAuditorsRow, setManageAuditorsRow] = useState(null);

    /** Fetching audits */
    const fetchAudits = useCallback(async () => {
        setLoading(true);
        try {
            const { audits } = await dispatch(
                actions.auditsList.create.getRequest(
                    currentStatus,
                    page + 1,
                    pageSize,
                    searchQuery,
                    ""
                )
            );
            setRowCount(audits.count !== 0 ? audits.count : 10);
            setLoading(false);
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On loading audits:", { error });
            dispatch(
                actions.toast.create.error(
                    strings("errors.onLoadAudits", { status: currentStatus })
                )
            );
            setLoading(false);
        }
    }, [page, pageSize, searchQuery, currentStatus]);

    useEffect(() => {
        fetchAudits();
    }, [fetchAudits]);

    /** Editing row */
    const onAuditReview = (row) => {
        try {
            const auditPath = pathname.split("/").slice(0, -1).join("/");
            navigate(`${auditPath}${"/"}${row.id}`);
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On audit path navigation:", { error });
            dispatch(actions.toast.create.error(strings("errors.onAuditPath")));
        }
    };

    const onArchiveAudit = (row) => (event) => {
        event?.stopPropagation();
        try {
            setCurrentRow(row);
            setArchiveDialog(true);
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On archive audit:", { error });
            dispatch(actions.toast.create.error(strings("errors.onArchive")));
        }
    };

    const onMultipleArchive = (selectedRowData) => {
        if (!selectedRowData?.length) return;
        setCurrentRow(selectedRowData.map((r) => r.id));
        setArchiveDialog(true);
    };

    const closeArchiveDialog = () => {
        setCurrentRow(null);
        setArchiveDialog(false);
        setSelectionResetToken((t) => t + 1);
    };

    const onAbandonAudit = (row) => (event) => {
        event?.stopPropagation();
        setAbandonRow(row);
        setAbandonDialog(true);
    };

    const closeAbandonDialog = () => {
        setAbandonRow(null);
        setAbandonDialog(false);
        setSelectionResetToken((t) => t + 1);
    };

    /** Handle email reminder */
    const handleEmailReminder = (row) => async () => {
        const audits = Array.isArray(row) ? row : [row.id];
        try {
            await dispatch(
                actions.auditResendAssignmentEmail.create.postRequest(audits)
            );
            dispatch(actions.toast.create.success("Reminder email sent!"));
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On sending reminder:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onSendReminder"))
            );
        }
    };

    const onMultipleEmailReminder = (selectedRowData) => {
        if (!selectedRowData?.length) return;
        handleEmailReminder(selectedRowData.map((r) => r.id))();
    };

    const onMultipleAbandon = (selectedRowData) => {
        if (!selectedRowData?.length) return;
        setAbandonRow(selectedRowData);
        setAbandonDialog(true);
    };

    /** Status filter */
    const statusFilter = useMemo(
        () => typesList.auditStatus.data.map((item) => item.name),
        [typesList]
    );

    /** Archive reason filter options */
    const archiveReasonFilter = useMemo(
        () => typesList?.archiveReasons?.data?.map((item) => item.name),
        [typesList]
    );

    /** Check for closed audit */
    const closedAudit = useMemo(
        () => typesList.auditStatus.data.find((item) => item.name === "closed"),
        [typesList]
    );

    /** Generate Status chip */
    const genChipStatus = (status) => {
        if (!status) return null;
        let chipStyles = styles.greenChip;
        if (status.id === 2 || status.id === 3) chipStyles = styles.blueChip;
        if (status.id === 4 || status.id === 5) chipStyles = styles.greenChip;
        return <Chip variant="caption" sx={chipStyles} label={status.name} />;
    };

    /** Remove columns per status tab */
    const updateCols = (cols) => {
        if (currentStatus === "archived")
            return cols.filter(
                (el) => !["assets", "closedDate"].includes(el.id)
            );
        if (currentStatus === "scheduled")
            return cols.filter(
                (el) =>
                    ![
                        "assets",
                        "status",
                        "closedDate",
                        "archivedReason",
                        "archivedNote",
                        "archivedDate"
                    ].includes(el.id)
            );
        if (currentStatus === "closed")
            return cols.filter(
                (el) =>
                    ![
                        "status",
                        "archivedReason",
                        "archivedNote",
                        "archivedDate"
                    ].includes(el.id)
            );
        // inprogress / draft
        return cols.filter(
            (el) =>
                ![
                    "closedDate",
                    "archivedReason",
                    "archivedNote",
                    "archivedDate"
                ].includes(el.id)
        );
    };

    /** Columns */
    const columns = useMemo(() => {
        const cols = [
            {
                id: "name",
                accessorKey: "name",
                header: strings("name"),
                Cell: ({ row }) => {
                    if (row.original.isGroup) {
                        return (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 700,
                                        color: "text.primary",
                                        minWidth: 90
                                    }}
                                >
                                    {row.original.name}
                                </Typography>
                                <Chip
                                    label={`${
                                        row.subRows?.length ?? 0
                                    } ${strings("audits")}`}
                                    size="small"
                                    color="primary"
                                    onClick={row.getToggleExpandedHandler()}
                                    sx={{
                                        fontWeight: 600,
                                        cursor: "pointer",
                                        "& .MuiChip-label": { color: "white" }
                                    }}
                                />
                            </Box>
                        );
                    }
                    return (
                        <Typography variant="caption" title={row.original.name}>
                            {row.original.name}
                        </Typography>
                    );
                }
            },
            {
                id: "auditors",
                accessorFn: (row) => row.auditors?.join(", ") ?? "",
                header: strings("auditors"),
                enableSorting: false,
                enableGlobalFilter: false,
                filterVariant: "autocomplete",
                filterFn: (row, _columnId, filterValue) => {
                    if (!filterValue) return true;
                    const auditors = row.original.auditors;
                    if (!auditors?.length) return false;
                    return auditors.some((a) =>
                        a.toLowerCase().includes(filterValue.toLowerCase())
                    );
                },
                Cell: ({ row }) => {
                    if (row.original.isGroup) return <ShowMore data={row.original.auditors} title={"auditors"} />;
                    const auditorCount = row.original.auditors?.length ?? 0;
                    const isReadOnly = currentStatus === "closed" || currentStatus === "archived" || currentStatus === "planned";
                    const AuditorIcon = auditorCount === 1 ? PersonIcon : GroupIcon;
                    return (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Tooltip title={isReadOnly ? "" : strings("manageAuditors")}>
                                <span>
                                    <IconButton
                                        size="small"
                                        disabled={isReadOnly}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setManageAuditorsRow(row.original);
                                        }}
                                        sx={{ color: isReadOnly ? "action.disabled" : "primary.main" }}
                                    >
                                        <AuditorIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <ShowMore data={row.original.auditors} title={"auditors"} />
                        </Box>
                    );
                }
            },
            {
                id: "scheduledEnd",
                accessorFn: (row) => row._rawEndDate,
                header: strings("due"),
                filterVariant: "date-range",
                filterFn: dateRangeFilterFn,
                sortingFn: "datetime",
                enableGlobalFilter: false,
                Cell: ({ row }) => {
                    const days = ["today", "tomorrow", "yesterday"];
                    const found = days.find((v) =>
                        row.original.scheduledEnd?.toLowerCase().includes(v)
                    );
                    return (
                        <Typography
                            variant="caption"
                            title={`Due at ${row.original.scheduledEnd}`}
                            style={{ color: found ? "red" : "inherit" }}
                        >
                            {row.original.scheduledEnd}
                        </Typography>
                    );
                }
            },
            {
                id: "assets",
                accessorFn: () => "",
                header: strings("assets"),
                enableColumnFilter: false,
                enableSorting: false,
                enableGlobalFilter: false,
                Cell: ({ row }) =>
                    row.original.assetSummary ? (
                        <AssetProgresBar data={row.original} />
                    ) : null
            },
            {
                id: "status",
                accessorKey: "status",
                header: strings("status"),
                filterVariant: "multi-select",
                filterSelectOptions: statusFilter,
                Cell: ({ row }) => genChipStatus(row.original.statusDetails)
            },
            {
                id: "filter",
                accessorFn: () => "",
                header: strings("filter"),
                enableSorting: false,
                enableColumnFilter: false,
                enableGlobalFilter: false,
                Cell: ({ row }) => {
                    const chips = getAuditFilterChips(
                        row.original.filter,
                        typesList
                    );
                    if (chips.length === 0) return null;
                    return (
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                                alignItems: "center"
                            }}
                        >
                            {chips.map((chip) => (
                                <Tooltip
                                    key={chip.key}
                                    title={chip.tooltip ?? ""}
                                    disableHoverListener={!chip.tooltip}
                                >
                                    <Chip
                                        label={chip.label}
                                        size="small"
                                        sx={{
                                            fontSize: "0.70rem",
                                            height: "22px",
                                            backgroundColor: "rgba(0,0,0,0.06)",
                                            border: "none",
                                            color: "text.secondary",
                                            "& .MuiChip-label": { px: 1 }
                                        }}
                                    />
                                </Tooltip>
                            ))}
                        </Box>
                    );
                }
            },
            {
                id: "branch",
                accessorKey: "branch",
                header: strings("branch"),
                enableColumnFilter: false,
                Cell: ({ row }) => (
                    <Typography variant="caption" title={row.original.branch}>
                        {row.original.branch}
                    </Typography>
                )
            },
            {
                id: "branchIds",
                accessorKey: "branchIds",
                header: strings("branchId"),
                enableColumnFilter: false,
                Cell: ({ row }) => (
                    <Typography variant="caption">
                        {row.original.branchIds}
                    </Typography>
                )
            },
            {
                id: "created",
                accessorFn: (row) => row._rawCreatedDate,
                header: strings("createdDate"),
                filterVariant: "date-range",
                filterFn: dateRangeFilterFn,
                sortingFn: "datetime",
                enableGlobalFilter: false,
                Cell: ({ row }) => (
                    <Typography variant="caption">
                        {row.original.createdDate}
                    </Typography>
                )
            },
            {
                id: "scheduledStart",
                accessorFn: (row) => row._rawStartDate,
                header: strings("scheduledStartDate"),
                filterVariant: "date-range",
                filterFn: dateRangeFilterFn,
                sortingFn: "datetime",
                enableGlobalFilter: false,
                Cell: ({ row }) => (
                    <Typography variant="caption">
                        {row.original.scheduledStart}
                    </Typography>
                )
            },
            {
                id: "closedDate",
                accessorFn: (row) => row._rawClosedDate,
                header: strings("closedDate"),
                filterVariant: "date-range",
                filterFn: dateRangeFilterFn,
                sortingFn: "datetime",
                enableGlobalFilter: false,
                Cell: ({ row }) => (
                    <Typography variant="caption">
                        {row.original.closedDate}
                    </Typography>
                )
            },
            {
                id: "archivedReason",
                accessorKey: "archivedReason",
                header: strings("archiveReason"),
                filterVariant: "multi-select",
                filterSelectOptions: archiveReasonFilter,
                Cell: ({ row }) => (
                    <Typography variant="caption" sx={styles.capitaliazeText}>
                        {row.original.archivedReason || "—"}
                    </Typography>
                )
            },
            {
                id: "archivedNote",
                accessorKey: "archivedNote",
                header: strings("archiveNote"),
                enableColumnFilter: false,
                Cell: ({ row }) => (
                    <Typography variant="caption">
                        {row.original.archivedNote || "—"}
                    </Typography>
                )
            },
            {
                id: "comment",
                accessorKey: "comment",
                header: strings("comment"),
                enableColumnFilter: false,
                Cell: ({ row }) => (
                    <Typography variant="caption">
                        {row.original.comment || "—"}
                    </Typography>
                )
            },
            {
                id: "archivedDate",
                accessorFn: (row) => row._rawArchivedDate,
                header: strings("archivedDate"),
                filterVariant: "date-range",
                filterFn: dateRangeFilterFn,
                sortingFn: "datetime",
                enableGlobalFilter: false,
                Cell: ({ row }) => (
                    <Typography variant="caption">
                        {row.original.archivedDate}
                    </Typography>
                )
            }
        ];
        return updateCols(cols);
    }, [statusFilter, archiveReasonFilter, typesList, currentStatus]);

    /** Row actions (archive / reminder / abandon / view per row) */
    const rowActions = (row) => {
        if (row.original.isGroup) {
            const subRowData = row.subRows?.map((r) => r.original) ?? [];
            const canReminder = subRowData.length > 0 && subRowData.every((r) => r.actions?.reminderEmail);
            const canAbandon = subRowData.length > 0 && subRowData.every((r) => r.actions?.abandonAudit);
            const canArchive = subRowData.length > 0 && !subRowData.every((r) => r.archived);
            return (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "nowrap" }}>
                    {canReminder ? (
                        <IconButton size="small" onClick={() => onMultipleEmailReminder(subRowData)}>
                            <Tooltip title={strings("sendReminder")}>
                                <NotificationsNoneOutlinedIcon fontSize="small" color="primary" />
                            </Tooltip>
                        </IconButton>
                    ) : <Box sx={{ width: 30 }} />}
                    {canAbandon ? (
                        <IconButton size="small" onClick={() => onMultipleAbandon(subRowData)}>
                            <Tooltip title={strings("abandonAudit")}>
                                <BlockOutlinedIcon fontSize="small" color="primary" />
                            </Tooltip>
                        </IconButton>
                    ) : <Box sx={{ width: 30 }} />}
                    {canArchive ? (
                        <IconButton size="small" onClick={() => onMultipleArchive(subRowData)}>
                            <Tooltip title={strings("archive")}>
                                <Inventory2OutlinedIcon fontSize="small" color="primary" />
                            </Tooltip>
                        </IconButton>
                    ) : <Box sx={{ width: 30 }} />}
                    <Box sx={{ width: 46 }} />
                </Box>
            );
        }

        const { archived, reminderEmail, abandonAudit } =
            row.original.actions ?? {};
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexWrap: "nowrap"
                }}
            >
                {!archived && reminderEmail ? (
                    <IconButton
                        size="small"
                        onClick={handleEmailReminder(row.original)}
                    >
                        <Tooltip title={strings("sendReminder")}>
                            <NotificationsNoneOutlinedIcon
                                fontSize="small"
                                color="primary"
                            />
                        </Tooltip>
                    </IconButton>
                ) : (
                    <Box sx={{ width: 30 }} />
                )}

                {!archived && abandonAudit ? (
                    <IconButton
                        size="small"
                        onClick={onAbandonAudit(row.original)}
                    >
                        <Tooltip title={strings("abandonAudit")}>
                            <BlockOutlinedIcon
                                fontSize="small"
                                color="primary"
                            />
                        </Tooltip>
                    </IconButton>
                ) : (
                    <Box sx={{ width: 30 }} />
                )}

                {!archived ? (
                    <IconButton
                        size="small"
                        onClick={onArchiveAudit(row.original)}
                    >
                        <Tooltip title={strings("archive")}>
                            <Inventory2OutlinedIcon
                                fontSize="small"
                                color="primary"
                            />
                        </Tooltip>
                    </IconButton>
                ) : (
                    <Box sx={{ width: 30 }} />
                )}

                {!archived && row.original.statusDetails?.id < 7 ? (
                    <Button
                        sx={styles.viewButton}
                        size="small"
                        onClick={() => onAuditReview(row.original)}
                    >
                        {strings("view")}
                    </Button>
                ) : (
                    <Box sx={{ width: 46 }} />
                )}
            </Box>
        );
    };

    /** Columns hidden by default */
    const tableInitialState = {
        columnVisibility: {
            branch: false,
            branchIds: false,
            created: false,
            scheduledStart: false,
            comment: false
        }
    };

    /** Combine branchesRef */
    const getBranchRefs = (branchId) => {
        if (branchId) {
            const index = branches.byId[branchId];
            const branchesRef = [];
            if (branches.data[index]) {
                branches.data[index].branchRef.map((item) => {
                    branchesRef.push(item.name);
                });
            }
            return branchesRef.join(",");
        } else return [];
    };

    /** Get auditors name */
    const getAuditors = (data) => {
        return data.map((item) =>
            item && item.firstName ? `${item.firstName} ${item.surname}` : ""
        );
    };

    /** Data for the audits */
    const data = useMemo(() => {
        const getStatus = (data, statusId) => {
            const res = helpers.getListItemForId(data, statusId);
            return res && Object.keys(res).length > 0
                ? res
                : { id: "", name: "", guid: "" };
        };

        const mapped = auditsList.data.map((item) => ({
            auditors: getAuditors(item.auditors),
            assetSummary: {
                altAsset: item.altAssetsSummary,
                auditAsset: {
                    unchecked: item.auditAssetsSummary.outstanding,
                    ...item.auditAssetsSummary
                }
            },
            archived: item.isArchived,
            archivedReason:
                item.archiveSummary.length > 0
                    ? getStatus(
                          typesList.archiveReasons,
                          item.archiveSummary[0].reason
                      ).name
                    : false,
            archivedDate:
                item.archiveSummary.length > 0
                    ? `${format.displayDatesWithTime(
                          item.archiveSummary[0].createdDate
                      )}`
                    : false,
            archivedNote:
                item.archiveSummary.length > 0
                    ? item.archiveSummary[0].note ?? ""
                    : "",
            comment: item.latestComment?.content ?? "",
            _rawArchivedDate:
                item.archiveSummary.length > 0
                    ? new Date(item.archiveSummary[0].createdDate)
                    : null,
            actions: {
                ...auditStatusDetail[item.status],
                archived: item.isArchived
            },
            branch: helpers.getListItemForId(branches, item.branch).name,
            branchIds: getBranchRefs(item.branch),
            createdDate: `${format.displayDatesWithTime(item.createdDate)}`,
            _rawCreatedDate: item.createdDate
                ? new Date(item.createdDate)
                : null,
            filter: item.filter ?? null,
            id: item.id,
            name: item.name,
            scheduledEnd: `${format.displayDatesWithTime(item.endDate)}`,
            scheduledStart: `${format.displayDatesWithTime(item.startDate)}`,
            _rawStartDate: item.startDate ? new Date(item.startDate) : null,
            closedDate: item.closedDate
                ? `${format.displayDatesWithTime(item.closedDate)}`
                : "",
            _rawClosedDate: item.closedDate ? new Date(item.closedDate) : null,
            status: getStatus(typesList.auditStatus, item.status).name,
            statusDetails: getStatus(typesList.auditStatus, item.status),
            auditReference: item.auditReference ?? null,
            brandOperationCode:
                item.brandOperationSummary?.brandOperationCode ?? null,
            branchType: item.brandOperationSummary?.branchType ?? null,
            _rawEndDate: item.endDate ? new Date(item.endDate) : null
        }));

        // Audits with no auditReference stay as standalone rows
        const standalone = mapped.filter((a) => !a.auditReference);

        // Audits with the same auditReference are grouped together
        const withReference = mapped.filter((a) => a.auditReference);
        const grouped = groupBy(withReference, "auditReference");
        const aggregateAssets = (audits) => ({
            auditAsset: [
                "unchecked",
                "seen",
                "excluded",
                "approved",
                "rejected",
                "total"
            ].reduce(
                (acc, key) => ({
                    ...acc,
                    [key]: audits.reduce(
                        (s, a) => s + (a.assetSummary?.auditAsset?.[key] || 0),
                        0
                    )
                }),
                {}
            ),
            altAsset: {
                total: audits.reduce(
                    (s, a) => s + (a.assetSummary?.altAsset?.total || 0),
                    0
                )
            }
        });

        const groupRows = Object.entries(grouped).map(([code, audits]) => {
            // Earliest due date across the group
            const earliestAudit = [...audits].sort((a, b) => {
                const da = a._rawEndDate ? new Date(a._rawEndDate).getTime() : Infinity;
                const db = b._rawEndDate ? new Date(b._rawEndDate).getTime() : Infinity;
                return da - db;
            })[0];

            // Group status comes from the branchType-priority audit: 1, else 2, else 3, else first child
            const statusSource =
                audits.find((a) => a.branchType === 1) ??
                audits.find((a) => a.branchType === 2) ??
                audits.find((a) => a.branchType === 3) ??
                audits[0];

            return {
                id: `group-${code}`,
                isGroup: true,
                name: audits[0]?.brandOperationCode ?? code,
                auditReference: code,
                brandOperationCode: audits[0]?.brandOperationCode ?? code,
                auditors: [...new Set(audits.flatMap((a) => a.auditors || []))],
                assetSummary: aggregateAssets(audits),
                status: statusSource?.status ?? null,
                statusDetails: statusSource?.statusDetails ?? null,
                scheduledEnd: earliestAudit?.scheduledEnd ?? "",
                _rawEndDate: earliestAudit?._rawEndDate ?? null,
                filter: null,
                branch: "",
                branchIds: "",
                createdDate: "",
                scheduledStart: "",
                closedDate: "",
                archivedReason: false,
                archivedDate: false,
                archivedNote: "",
                comment: "",
                archived: false,
                actions: { archived: true, reminderEmail: false },
                subRows: audits
            };
        });

        return [...standalone, ...groupRows];
    }, [branches, auditsList, typesList]);

    // Group rows are not selectable — only real audit rows can be selected/archived
    const enableRowSelection = (row) => !row.original.isGroup;

    // Tells MRT where to find children for tree-data rows
    const getSubRows = (row) => row.subRows ?? [];

    return {
        columns,
        closeArchiveDialog,
        closeAbandonDialog,
        currentStatus,
        currentRow,
        abandonRow,
        abandonDialog,
        data,
        fetchAudits,
        loading,
        archiveDialog,
        onMultipleArchive,
        onMultipleEmailReminder,
        onMultipleAbandon,
        manageAuditorsRow,
        setManageAuditorsRow,
        onArchiveAudit,
        onAuditReview,
        rowActions,
        tableInitialState,
        persistKey,
        selectionResetToken,
        enableRowSelection,
        getSubRows,
        page,
        pageSize,
        rowCount,
        setPage,
        setPageSize,
        setSearchQuery,
        setLoading
    };
};
